import { formatTokenCount } from '../../utils/formatTokenCount';
import { describeCoworkContext } from '../../utils/coworkContextView';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import type { CoworkSession } from '../../types/cowork';
import { i18nService } from '../../services/i18n';


const CoworkTokenBar: React.FC<{ session: CoworkSession }> = ({ session }) => {
  const availableModels = useSelector((state: RootState) => state.model.availableModels);
  const view = useMemo(() => describeCoworkContext(session, availableModels), [session, availableModels]);
  const hasInvalidLegacyUsage = view.used === null;
  const limitLabel = view.limit === null ? '--' : formatTokenCount(view.limit);
  const usedLabel = view.used === null ? '--' : formatTokenCount(view.used);
  const percentage = view.used === null || !view.limit ? 0 : (view.used / view.limit) * 100;
  const stateLabels = {
    current: `${Math.round(percentage)}%`,
    compacting: i18nService.t('coworkContextCompacting'),
    pending: i18nService.t('coworkContextPending'),
    unknown: i18nService.t('coworkContextUnknown'),
  };
  const percentageLabel = stateLabels[view.state];
  const inputTokens = session.usage?.inputTokens ?? 0;
  const cacheCreationTokens = session.usage?.cacheCreationTokens ?? 0;
  const cacheReadTokens = session.usage?.cacheReadTokens ?? 0;
  const color = percentage > 80 ? '#ef4444' : percentage > 60 ? '#f59e0b' : '#3b82f6';
  const title = hasInvalidLegacyUsage
    ? `${i18nService.t('coworkContextUsage')}: ${percentageLabel} / ${limitLabel}`
    : [
      `${i18nService.t('coworkContextUsage')}: ${usedLabel} / ${limitLabel}`,
      `${i18nService.t('coworkUncachedInput')}: ${formatTokenCount(inputTokens)}`,
      `${i18nService.t('coworkCacheWrite')}: ${formatTokenCount(cacheCreationTokens)}`,
      `${i18nService.t('coworkCacheRead')}: ${formatTokenCount(cacheReadTokens)}`,
    ].join('\n');
  const accessibleTitle = `${title}${view.modelId ? `\n${view.modelId}` : ''}`;

  return (
    <div className="flex shrink-0 items-center gap-1.5 whitespace-nowrap" title={accessibleTitle} role="status" aria-label={accessibleTitle} data-context-state={view.state} tabIndex={0}>
      <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" className="shrink-0 -rotate-90">
        <circle cx="10" cy="10" r="7" fill="none" stroke="currentColor" strokeWidth="2" className="text-claude-border dark:text-claude-darkBorder" />
        <circle cx="10" cy="10" r="7" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round"
          strokeDasharray={2 * Math.PI * 7}
          strokeDashoffset={2 * Math.PI * 7 * (1 - Math.min(100, Math.max(0, percentage)) / 100)}
          className="transition-[stroke-dashoffset] duration-300 motion-reduce:transition-none" />
      </svg>
    </div>
  );
};

export default CoworkTokenBar;
